<div class="form-demo-container">
  <!-- Header avec contrôles globaux -->
  <div class="demo-header">
    <div class="header-section">
      <h2>🎯 Démo Formulaire Multi-Langues</h2>
      <div class="status-indicators">
        <div class="save-status" [class.saving]="autoSaveStatus.saveInProgress" [class.error]="autoSaveStatus.saveError">
          <span class="status-icon">{{ getStatusIcon() }}</span>
          <span class="status-text">
            <ng-container *ngIf="autoSaveStatus.saveInProgress">Sauvegarde...</ng-container>
            <ng-container *ngIf="autoSaveStatus.saveError">{{ autoSaveStatus.saveError }}</ng-container>
            <ng-container *ngIf="!autoSaveStatus.saveInProgress && !autoSaveStatus.saveError && autoSaveStatus.lastSaveTime">
              Sauvé {{ formatTimeAgo(autoSaveStatus.lastSaveTime) }}
            </ng-container>
            <ng-container *ngIf="!autoSaveStatus.saveInProgress && !autoSaveStatus.saveError && !autoSaveStatus.lastSaveTime">
              Pas encore sauvé
            </ng-container>
          </span>
        </div>
        
        <div class="autosave-controls">
          <button class="btn-toggle" 
                  [class.active]="autoSaveStatus.isActive" 
                  (click)="toggleAutoSave()">
            <span *ngIf="autoSaveStatus.isActive">🟢 Sur changement</span>
            <span *ngIf="!autoSaveStatus.isActive">⭕ Manuel</span>
          </button>
          <span *ngIf="autoSaveStatus.isActive" class="delay-info">
            Délai: {{ autoSaveStatus.debounceDelay }}ms
          </span>
        </div>
      </div>
    </div>

    <!-- Contrôles de sauvegarde et historique -->
    <div class="header-controls">
      <div class="save-controls">
        <button class="btn-primary" (click)="manualSave()" [disabled]="autoSaveStatus.saveInProgress">
          💾 Sauvegarder
        </button>
        
        <select (change)="onDebounceDelayChange($event)" [value]="autoSaveStatus.debounceDelay">
          <option value="500">0.5s</option>
          <option value="1000">1s</option>
          <option value="2000">2s</option>
          <option value="3000">3s</option>
        </select>
      </div>

      <div class="history-controls">
        <button class="btn-secondary" (click)="undo()" [disabled]="!historyState.canUndo" title="Ctrl+Z" data-testid="history-button-undo">
          ↶ Annuler
        </button>
        <button class="btn-secondary" (click)="redo()" [disabled]="!historyState.canRedo" title="Ctrl+Y" data-testid="history-button-redo">
          ↷ Rétablir
        </button>
        <span class="history-info">{{ historyState.currentIndex + 1 }}/{{ historyState.entries.length }}</span>
      </div>
    </div>
  </div>

  <div class="demo-content">
    <!-- Sidebar avec gestion des langues et historique -->
    <div class="sidebar">
      <!-- Gestion des langues -->
      <div class="sidebar-section">
        <h3>🌐 Langues</h3>
        
        <div class="language-selector">
          <div class="current-language">
            <select [(ngModel)]="currentLanguage" (change)="switchLanguage(currentLanguage)" data-testid="lang-select">
              <option *ngFor="let lang of availableLanguages" [value]="lang">
                {{ lang.toUpperCase() }}
                <span *ngIf="lang === primaryLanguage">(Principal)</span>
              </option>
            </select>
          </div>
          
          <div class="language-actions">
            <button class="btn-small" (click)="addLanguage()" title="Ajouter une langue">
              ➕
            </button>
            <button class="btn-small" 
                    (click)="removeLanguage(currentLanguage)" 
                    [disabled]="currentLanguage === primaryLanguage"
                    title="Supprimer cette langue">
              ➖
            </button>
            <button class="btn-small" 
                    (click)="setPrimaryLanguage(currentLanguage)"
                    [disabled]="currentLanguage === primaryLanguage"
                    title="Définir comme langue principale">
              ⭐
            </button>
          </div>
        </div>

        <div class="language-sync" *ngIf="currentLanguage !== primaryLanguage">
          <button class="btn-small btn-sync" (click)="cloneFromPrimary()">
            🔄 Copier depuis {{ primaryLanguage.toUpperCase() }}
          </button>
        </div>
      </div>

      <!-- Historique récent -->
      <div class="sidebar-section">
        <h3>📚 Historique</h3>
        
        <div class="history-list">
          <div *ngFor="let entry of recentHistory; trackBy: trackByEntryId" 
               class="history-entry"
               [class.current]="historyState.entries[historyState.currentIndex]?.id === entry.id"
               (click)="jumpToHistoryEntry(entry)">
            <div class="entry-type">
              <span *ngIf="entry.type === 'auto'">🔄</span>
              <span *ngIf="entry.type === 'manual'">💾</span>
              <span *ngIf="entry.type === 'initial'">🎯</span>
            </div>
            <div class="entry-info">
              <div class="entry-time">{{ formatTimeAgo(entry.timestamp) }}</div>
              <div class="entry-desc">{{ entry.description }}</div>
            </div>
          </div>
        </div>

        <button class="btn-small btn-danger" (click)="clearHistory()" *ngIf="historyState.entries.length > 0">
          🗑️ Effacer l'historique
        </button>
      </div>
    </div>

    <!-- Formulaire principal -->
    <div class="main-form">
      <form [formGroup]="demoForm" class="demo-form">
        
        <!-- Informations générales -->
        <div class="form-section">
          <h3>📝 Informations générales</h3>
          
          <div class="form-group">
            <label for="title">Titre *</label>
            <input id="title" 
                   type="text" 
                   formControlName="title" 
                   placeholder="Saisissez le titre..."
                   data-testid="field-input-title"
                   [class.error]="getFieldError('title')">
            <div class="error-message" *ngIf="getFieldError('title')">
              {{ getFieldError('title') }}
            </div>
          </div>

          <div class="form-group">
            <label for="description">Description *</label>
            <textarea id="description" 
                      formControlName="description" 
                      rows="4"
                      placeholder="Décrivez votre contenu..."
                      data-testid="field-input-description"
                      [class.error]="getFieldError('description')"></textarea>
            <div class="error-message" *ngIf="getFieldError('description')">
              {{ getFieldError('description') }}
            </div>
          </div>

          <div class="form-group">
            <label for="category">Catégorie *</label>
            <select id="category" formControlName="category" [class.error]="getFieldError('category')">
              <option value="">Choisir une catégorie...</option>
              <option *ngFor="let option of categoryOptions" [value]="option.value">
                {{ option.label }}
              </option>
            </select>
            <div class="error-message" *ngIf="getFieldError('category')">
              {{ getFieldError('category') }}
            </div>
          </div>

          <div class="form-group">
            <label>Étiquettes</label>
            <div class="tags-container">
              <div class="tags-list">
                <span *ngFor="let tag of demoForm.get('tags')?.value; let i = index" 
                      class="tag"
                      (click)="removeTag(i)">
                  {{ tag }} ✕
                </span>
              </div>
              <button type="button" class="btn-small" (click)="addTag()">
                ➕ Ajouter
              </button>
            </div>
          </div>
        </div>

        <!-- Contenu -->
        <div class="form-section" formGroupName="content">
          <h3>📄 Contenu</h3>
          
          <div class="form-group">
            <label for="welcomeMessage">Message d'accueil</label>
            <input id="welcomeMessage" 
                   type="text" 
                   formControlName="welcomeMessage" 
                   data-testid="field-input-welcomeMessage"
                   placeholder="Bienvenue...">
          </div>

          <div class="form-group">
            <label for="instructions">Instructions</label>
            <textarea id="instructions" 
                      formControlName="instructions" 
                      rows="3"
                      placeholder="Expliquez comment utiliser..."></textarea>
          </div>

          <div class="form-group">
            <label for="footerText">Texte de pied de page</label>
            <input id="footerText" 
                   type="text" 
                   formControlName="footerText" 
                   placeholder="© 2024...">
          </div>
        </div>

        <!-- Paramètres -->
        <div class="form-section" formGroupName="settings">
          <h3>⚙️ Paramètres</h3>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" formControlName="enableNotifications">
              <span class="checkmark"></span>
              Activer les notifications
            </label>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" formControlName="autoSave">
              <span class="checkmark"></span>
              Sauvegarde automatique
            </label>
          </div>

          <div class="form-group">
            <label for="theme">Thème</label>
            <select id="theme" formControlName="theme">
              <option *ngFor="let option of themeOptions" [value]="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

      </form>
    </div>
  </div>

  <!-- Debug info (en bas) -->
  <div class="debug-info" *ngIf="false">
    <details>
      <summary>🔧 Informations de debug</summary>
      <div class="debug-content">
        <div><strong>Langue actuelle:</strong> {{ currentLanguage }}</div>
        <div><strong>Langue principale:</strong> {{ primaryLanguage }}</div>
        <div><strong>Formulaire valide:</strong> {{ demoForm.valid ? '✅' : '❌' }}</div>
        <div><strong>Statut sauvegarde:</strong> {{ autoSaveStatus.isActive ? 'Active' : 'Inactive' }}</div>
        <div><strong>Historique:</strong> {{ historyState.entries.length }} entrées</div>
        
        <pre>{{ demoForm.value | json }}</pre>
      </div>
    </details>
  </div>
</div>